import { create } from "zustand";
import { RequestStatus } from "../utils/commons";
import type {
  ICashDepositContext,
  IFundingAttempt,
  IFundingInstrument,
  IFundingMerchant,
} from "../interfaces/cash-deposit";

interface ICashDepositState {
  context: ICashDepositContext | null;
  status: RequestStatus;
  merchants: IFundingMerchant[];
  selectedMerchantId: string | null;
  instrument: IFundingInstrument | null;
  seenFundingAttemptKeys: string[];
  pendingMismatch: IFundingAttempt | null;
  /** A background barcode refresh (after an amount mismatch) is in flight. */
  refreshingBarcode: boolean;
  /** Attempt key (`attemptRef ?? occurredAt`) that already triggered a refresh. */
  refreshedMismatchKey: string | null;
  setContext: (context: ICashDepositContext) => void;
  setStatus: (status: RequestStatus) => void;
  setMerchants: (merchants: IFundingMerchant[]) => void;
  setSelectedMerchantId: (merchantId: string | null) => void;
  setInstrument: (instrument: IFundingInstrument | null) => void;
  markFundingAttemptSeen: (key: string) => void;
  setPendingMismatch: (attempt: IFundingAttempt | null) => void;
  setRefreshingBarcode: (refreshing: boolean) => void;
  setRefreshedMismatchKey: (key: string | null) => void;
  reset: () => void;
}

/**
 * Holds the cash-deposit context (sender, amount) resolved from the dedicated
 * backend endpoint after the link is claimed, plus the funding-location
 * (merchant) selection and the issued barcode instrument.
 */
export const useCashDepositStore = create<ICashDepositState>((set) => ({
  context: null,
  status: RequestStatus.NOT_LOADED,
  merchants: [],
  selectedMerchantId: null,
  instrument: null,
  seenFundingAttemptKeys: [],
  pendingMismatch: null,
  refreshingBarcode: false,
  refreshedMismatchKey: null,
  setContext: (context) =>
    set(() => ({ context, status: RequestStatus.LOADED })),
  setStatus: (status) => set(() => ({ status })),
  setMerchants: (merchants) => set(() => ({ merchants })),
  setSelectedMerchantId: (selectedMerchantId) =>
    set(() => ({ selectedMerchantId })),
  setInstrument: (instrument) => set(() => ({ instrument })),
  markFundingAttemptSeen: (key) =>
    set((state) =>
      state.seenFundingAttemptKeys.includes(key)
        ? state
        : { seenFundingAttemptKeys: [...state.seenFundingAttemptKeys, key] },
    ),
  setPendingMismatch: (pendingMismatch) => set(() => ({ pendingMismatch })),
  setRefreshingBarcode: (refreshingBarcode) => set(() => ({ refreshingBarcode })),
  setRefreshedMismatchKey: (refreshedMismatchKey) =>
    set(() => ({ refreshedMismatchKey })),
  reset: () =>
    set(() => ({
      context: null,
      status: RequestStatus.NOT_LOADED,
      merchants: [],
      selectedMerchantId: null,
      instrument: null,
      seenFundingAttemptKeys: [],
      pendingMismatch: null,
      refreshingBarcode: false,
      refreshedMismatchKey: null,
    })),
}));
